import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { ActiveStatusChoice } from '@/utils/filters';
import { DEFAULT_ZM_STATUS, ZM_STATUS_FILTER_KEY } from '../constants/dashboard.constants';
import type { SystemDashboardListSearch } from '../types/dashboard.types';
import {
  toReportingPeriodFilterSearch,
  toReportingPeriodFilterValues,
} from '../utils/reportingPeriodFilters';

const ROUTES = {
  system: getRouteApi('/_auth/dashboards/system/'),
  der: getRouteApi('/_auth/dashboards/der'),
} as const;

export type ReportingPeriodRouteId = keyof typeof ROUTES;

const DEFAULT_STATUS: Record<ReportingPeriodRouteId, ActiveStatusChoice | undefined> = {
  system: DEFAULT_ZM_STATUS,
  der: undefined,
};

const normalizeFilterValues = (values: Record<string, string>) =>
  toReportingPeriodFilterValues(toReportingPeriodFilterSearch(values));

export function useReportingPeriodFilters(routeId: ReportingPeriodRouteId) {
  const route = ROUTES[routeId];
  const defaultStatus = DEFAULT_STATUS[routeId];
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedSearch = useMemo(
    (): SystemDashboardListSearch => ({ ...search, zmStatus: search.zmStatus ?? defaultStatus }),
    [search, defaultStatus],
  );
  const appliedValues = useMemo(
    () => toReportingPeriodFilterValues(appliedSearch),
    [appliedSearch],
  );
  const defaultValues = useMemo(
    () => ({
      ...getMonthToDateRange(),
      ...(defaultStatus ? { [ZM_STATUS_FILTER_KEY]: defaultStatus } : {}),
    }),
    [defaultStatus],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toReportingPeriodFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): SystemDashboardListSearch & { dateFrom: string; dateTo: string } => ({
      ...appliedSearch,
      dateFrom: appliedSearch.dateFrom ?? defaultValues.dateFrom,
      dateTo: appliedSearch.dateTo ?? defaultValues.dateTo,
    }),
    [appliedSearch, defaultValues],
  );

  const zmSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...zmSearch, params, hasAllStatusChoice: defaultStatus !== undefined };
}
